// Project categories for filtering
export type ProjectCategory = 'all' | 'website' | 'landing' | 'product' | 'marketplace' | 'ai' | 'experimental';

export interface Project {
  id: string;
  title: string;
  subtitle: string;
  subtitleEn: string;
  description: string;
  descriptionEn: string;
  category: ProjectCategory;
  year: string;
  tags: string[];
  image: string;
  color: string; // Accent color for hover states
  featured?: boolean;
}

// Imágenes: sube tus screenshots en public/projects/ (nombres abajo).
// Rutas: en Vite todo lo que está en public/ se sirve desde la raíz, ej. /projects/nexa.png
const PROJECT_IMG = (project: string, file: string) => `/projects/${project}/${file.replace(/\.(png|jpe?g)$/i, '.webp')}`;

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600&q=90",
  nexa: PROJECT_IMG("nexa", "portada.png"),
  hogarMaestro: PROJECT_IMG("hogar-maestro", "portada.png"),
  ggstore: PROJECT_IMG("ggstore", "portada.png"),
  chulls: PROJECT_IMG("chulls", "portada.png"),
  instarender: PROJECT_IMG("instarender", "portada.png"),
  lumbra: PROJECT_IMG("lumbra", "portada.png"),
  invitaciones: PROJECT_IMG("invitaciones", "portada.png"),
  vetpe: PROJECT_IMG("vetpe", "portada.png"),
  motorperu: PROJECT_IMG("motorperu", "portada.png"),
  midefensaperu: PROJECT_IMG("midefensaperu", "portada.png"),
  sanMarcos: PROJECT_IMG("san-marcos", "portada.png"),
  sunatfacil: PROJECT_IMG("sunatfacil", "portada.png"),
  axon: PROJECT_IMG("axon", "portada.png"),
  hermesTech: PROJECT_IMG("hermes-tech", "portada.png"),
  peruAgroConnect: PROJECT_IMG("peru-agro-connect", "portada.png"),
  festora: PROJECT_IMG("festora", "portada.png"),
  odinShop: PROJECT_IMG("odin-shop", "portada.png"),
  zonifica: PROJECT_IMG("zonifica", "portada.png"),
  tradingDashboard: PROJECT_IMG("trading-dashboard", "portada.png"),

};

export const PROJECTS: Project[] = [
  {
    id: "peru-agro-connect",
    title: "PeruAgroConnect",
    subtitle: "Marketplace agroexportador con trazabilidad",
    subtitleEn: "Agri-export marketplace with traceability",
    description: "Conecta compradores internacionales con MYPEs agro peruanas: catálogo, perfiles de proveedor, lotes y trazabilidad QR, con CRM, cotizaciones y preparación exportadora.",
    descriptionEn: "Connects international buyers with Peruvian agri SMEs: catalog, supplier profiles, lots and QR traceability, plus CRM, quotes, and export-readiness tooling.",
    category: "marketplace",
    year: "2025",
    tags: ["Marketplace", "AgriTech", "Trazabilidad", "Export"],
    image: IMAGES.peruAgroConnect,
    color: "#16a34a",
    featured: true,
  },
  {
    id: "nexa",
    title: "Nexa",
    subtitle: "Gestión inteligente para consultorio dental",
    subtitleEn: "Smart management for dental clinics",
    description: "SaaS clínico para agenda, pacientes, tratamientos, pagos y operación diaria de una clínica dental.",
    descriptionEn: "Clinical SaaS for scheduling, patients, treatments, payments, and the daily operation of a dental clinic.",
    category: "product",
    year: "2024",
    tags: ["AI", "Health Tech", "SaaS"],
    image: IMAGES.nexa,
    color: "#0ea5e9",
    featured: true,
  },
  {
    id: "hogar-maestro",
    title: "Hogar Maestro",
    subtitle: "Sistema de gestión ERP",
    subtitleEn: "ERP management system",
    description: "ERP operativo para controlar inventario, compras, finanzas, usuarios y procesos internos.",
    descriptionEn: "Operational ERP to control inventory, purchasing, finance, users, and internal processes.",
    category: "product",
    year: "2024",
    tags: ["ERP", "Dashboard", "Auth"],
    image: IMAGES.hogarMaestro,
    color: "#6366f1",
    featured: true,
  },
  {
    id: "ggstore",
    title: "GGSTORE",
    subtitle: "Sistema de inventario y ventas",
    subtitleEn: "Inventory and sales system",
    description: "POS e inventario en tiempo real para ventas, stock, costos, márgenes y reportes de negocio.",
    descriptionEn: "Real-time POS and inventory for sales, stock, costs, margins, and business reporting.",
    category: "product",
    year: "2024",
    tags: ["SvelteKit", "PostgreSQL", "Drizzle"],
    image: IMAGES.ggstore,
    color: "#10b981",
    featured: true,
  },
  {
    id: "chulls",
    title: "CHULL'S",
    subtitle: "Peruvian cuisine & craft beer",
    subtitleEn: "Peruvian cuisine & craft beer",
    description: "Web gastronómica con dirección visual, carta, reservas y presencia digital para restaurante.",
    descriptionEn: "Restaurant website with visual direction, menu, reservations, and a strong digital presence.",
    category: "website",
    year: "2024",
    tags: ["Restaurant", "Reservas", "Gastronomía"],
    image: IMAGES.chulls,
    color: "#f59e0b",
    featured: true,
  },
  {
    id: "instarender",
    title: "InstaRender",
    subtitle: "AI architectural visualizations",
    subtitleEn: "AI architectural visualizations",
    description: "Experiencia web para estudio de renders arquitectónicos, con enfoque visual e inmersivo.",
    descriptionEn: "Web experience for an architectural rendering studio, with an immersive, visual focus.",
    category: "ai",
    year: "2025",
    tags: ["Three.js", "AI", "D5 Render"],
    image: IMAGES.instarender,
    color: "#8b5cf6",
    featured: true,
  },
  {
    id: "lumbra",
    title: "LUMBRA",
    subtitle: "Ingeniería y arquitectura",
    subtitleEn: "Engineering and architecture",
    description: "Sitio institucional para comunicar servicios técnicos, confianza profesional y portafolio arquitectónico.",
    descriptionEn: "Corporate site to communicate technical services, professional trust, and an architecture portfolio.",
    category: "website",
    year: "2024",
    tags: ["Interior Design", "Arquitectura"],
    image: IMAGES.lumbra,
    color: "#14b8a6",
    featured: false,
  },

  {
    id: "invitaciones",
    title: "Invitaciones de Eventos",
    subtitle: "Gestión de invitaciones y RSVP con 3D",
    subtitleEn: "Invitation and RSVP management with 3D",
    description: "Plataforma para eventos con confirmación de invitados, experiencia 3D y control de asistencia.",
    descriptionEn: "Event platform with guest confirmation, a 3D experience, and attendance tracking.",
    category: "product",
    year: "2024",
    tags: ["React", "Three.js", "RSVP", "WebGL"],
    image: IMAGES.invitaciones,
    color: "#ec4899",
    featured: false,
  },
  {
    id: "vetpe",
    title: "VetPe",
    subtitle: "Marketplace veterinario",
    subtitleEn: "Veterinary marketplace",
    description: "Plataforma para conectar clientes con servicios veterinarios, perfiles, reservas y operación multirol.",
    descriptionEn: "Platform connecting clients with veterinary services: profiles, bookings, and multi-role operation.",
    category: "marketplace",
    year: "2025",
    tags: ["Marketplace", "Payments", "Auth"],
    image: IMAGES.vetpe,
    color: "#22c55e",
    featured: true,
  },
  {
    id: "motorperu",
    title: "MotorPerú",
    subtitle: "Marketplace automotriz",
    subtitleEn: "Automotive marketplace",
    description: "Ecosistema para compra, venta y gestión de vehículos con backoffice, filtros y flujos comerciales.",
    descriptionEn: "Ecosystem to buy, sell, and manage vehicles, with a backoffice, filters, and commercial flows.",
    category: "marketplace",
    year: "2025",
    tags: ["Marketplace", "Vehicles", "Admin"],
    image: IMAGES.motorperu,
    color: "#ef4444",
    featured: true,
  },
  {
    id: "midefensaperu",
    title: "MiDefensaPerú",
    subtitle: "Marketplace legal",
    subtitleEn: "Legal marketplace",
    description: "Producto legal para conectar usuarios con abogados, gestionar casos, servicios y administración interna.",
    descriptionEn: "Legal product connecting users with lawyers to manage cases, services, and internal administration.",
    category: "marketplace",
    year: "2025",
    tags: ["Legal Tech", "Marketplace", "Backoffice"],
    image: IMAGES.midefensaperu,
    color: "#2563eb",
    featured: true,
  },
  {
    id: "san-marcos",
    title: "San Marcos Engine",
    subtitle: "Dashboard educativo",
    subtitleEn: "Educational dashboard",
    description: "Sistema de analítica y simulación para preparación académica, métricas, rendimiento y seguimiento.",
    descriptionEn: "Analytics and simulation system for exam prep, with metrics, performance, and progress tracking.",
    category: "product",
    year: "2025",
    tags: ["Dashboard", "Education", "Analytics"],
    image: IMAGES.sanMarcos,
    color: "#7c3aed",
  },
  {
    id: "sunatfacil",
    title: "SunatFácil",
    subtitle: "Automatización tributaria",
    subtitleEn: "Tax automation",
    description: "Herramienta para simplificar flujos tributarios, comprobantes, consultas y procesos administrativos.",
    descriptionEn: "Tool to simplify tax workflows, e-invoices, queries, and administrative processes.",
    category: "product",
    year: "2025",
    tags: ["Automation", "SaaS", "Peru"],
    image: IMAGES.sunatfacil,
    color: "#0891b2",
  },
  {
    id: "axon",
    title: "Axon Landing",
    subtitle: "Landing de producto AI",
    subtitleEn: "AI product landing",
    description: "Landing page orientada a conversión para presentar propuesta, beneficios y llamada a acción clara.",
    descriptionEn: "Conversion-focused landing page presenting the value proposition, benefits, and a clear call to action.",
    category: "landing",
    year: "2025",
    tags: ["Landing", "AI", "Conversion"],
    image: IMAGES.axon,
    color: "#f97316",
  },
  {
    id: "hermes-tech",
    title: "Hermes Tech",
    subtitle: "AI agents & workflows",
    subtitleEn: "AI agents & workflows",
    description: "Sitio para explicar capacidades de automatización, agentes, flujos internos y productos con IA.",
    descriptionEn: "Site explaining automation capabilities, agents, internal workflows, and AI products.",
    category: "ai",
    year: "2025",
    tags: ["AI Agents", "Automation", "Workflows"],
    image: IMAGES.hermesTech,
    color: "#a855f7",
  },
  {
    id: "festora",
    title: "Festora",
    subtitle: "Marketplace de proveedores de eventos",
    subtitleEn: "Event vendor marketplace",
    description: "Plataforma para organizar eventos conectando demanda con proveedores: catálogo, disponibilidad, cotización, pagos y panel de proveedor.",
    descriptionEn: "Platform to organize events by connecting demand with vendors: catalog, availability, quotes, payments, and a provider panel.",
    category: "marketplace",
    year: "2025",
    tags: ["Marketplace", "Events", "Payments"],
    image: IMAGES.festora,
    color: "#ea580c",
  },
  {
    id: "zonifica",
    title: "Zonifica.pe",
    subtitle: "Consultas urbanísticas inteligentes",
    subtitleEn: "Smart urban-planning queries",
    description: "Producto que simplifica consultas de zonificación, parámetros urbanísticos y licencias de edificación, convirtiendo normativa técnica en respuestas claras.",
    descriptionEn: "Product that simplifies zoning queries, urban-planning parameters, and building permits, turning technical regulation into clear answers.",
    category: "product",
    year: "2025",
    tags: ["LegalTech", "Urbanismo", "AI"],
    image: IMAGES.zonifica,
    color: "#f59e0b",
  },
  {
    id: "trading-dashboard",
    title: "Trading Dashboard",
    subtitle: "Dashboard de señales y disciplina",
    subtitleEn: "Signals and discipline dashboard",
    description: "Panel que convierte reglas y señales en una interfaz para decidir mejor: posiciones activas, alertas, bitácora y métricas en tiempo real.",
    descriptionEn: "Dashboard that turns rules and signals into an interface for better decisions: active positions, alerts, a journal, and real-time metrics.",
    category: "product",
    year: "2025",
    tags: ["Dashboard", "Fintech", "Analytics"],
    image: IMAGES.tradingDashboard,
    color: "#059669",
  },
  {
    id: "odin-shop",
    title: "Odin Shop",
    subtitle: "E-commerce para coleccionistas",
    subtitleEn: "E-commerce for collectors",
    description: "Tienda online con catálogo curado, navegación de productos y experiencia de compra directa para coleccionables premium.",
    descriptionEn: "Online store with a curated catalog, product browsing, and a direct purchase experience for premium collectibles.",
    category: "website",
    year: "2025",
    tags: ["E-commerce", "Storefront", "Retail"],
    image: IMAGES.odinShop,
    color: "#b91c1c",
  },
];

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Conocernos",
    description: "Nos reunimos para entender tu negocio.",
    details: "Nos cuentas cómo trabajas hoy, qué te frena y qué resultado te gustaría lograr."
  },
  {
    number: "02",
    title: "Plan",
    description: "Definimos la mejor solución para tu caso.",
    details: "Te proponemos una ruta clara: qué se hace primero, cuánto toma y cuánto cuesta."
  },
  {
    number: "03",
    title: "Desarrollo",
    description: "Construimos contigo, paso a paso.",
    details: "Te mostramos avances frecuentes para ajustar detalles antes de la entrega final."
  },
  {
    number: "04",
    title: "Entrega",
    description: "Publicamos y dejamos todo funcionando.",
    details: "Tu web o sistema queda operativo, con soporte continuo para mantenerlo estable en el tiempo."
  }
];

export const PROCESS_STEPS_EN = [
  {
    number: "01",
    title: "Discovery",
    description: "Deep business analysis.",
    details: "We identify AI optimization opportunities and define measurable ROI targets."
  },
  {
    number: "02",
    title: "Strategy",
    description: "Data-driven solution architecture.",
    details: "MVP scoping, stack selection, and integration planning aligned with growth goals."
  },
  {
    number: "03",
    title: "Build",
    description: "Agile development with continuous feedback.",
    details: "AI features, analytics dashboards, and scalable infrastructure built for enterprise."
  },
  {
    number: "04",
    title: "Launch",
    description: "Performance monitoring and optimization.",
    details: "Your investment keeps delivering compounding returns."
  }
];

export const SERVICES = [
  "Desarrollo web",
  "Diseño de producto (UI/UX)",
  "Integración de IA",
  "Aplicaciones móviles",
  "Identidad de marca",
  "Consultoría técnica",
  "Infraestructura cloud",
  "Desarrollo SaaS"
];

export const SERVICES_EN = [
  "Web development",
  "Product design (UI/UX)",
  "AI integration",
  "Mobile applications",
  "Brand identity",
  "Technical consulting",
  "Cloud infrastructure",
  "SaaS development"
];

export const COMPARISON_FEATURES = [
  { label: "Tiempo de entrega", agency: "3-6 meses", freelancer: "Impredecible", musashi: "4-8 semanas" },
  { label: "Comunicación", agency: "Llamadas semanales", freelancer: "Asíncrónica", musashi: "Slack Connect" },
  { label: "Calidad de diseño", agency: "Buena", freelancer: "Variable", musashi: "Clase mundial" },
  { label: "Stack tecnológico", agency: "Limitado", freelancer: "Variable", musashi: "El más rápido y eficiente para tu caso" },
  { label: "Escalabilidad", agency: "Actualizaciones costosas", freelancer: "Limitada", musashi: "Nivel enterprise" },
  { label: "Precio", agency: "$50k - $150k+", freelancer: "Tarifa por hora", musashi: "Precio fijo por alcance" }
];

export const COMPARISON_FEATURES_EN = [
  { label: "Delivery time", agency: "3-6 months", freelancer: "Unpredictable", musashi: "4-8 weeks" },
  { label: "Communication", agency: "Weekly calls", freelancer: "Async", musashi: "Slack Connect" },
  { label: "Design quality", agency: "Good", freelancer: "Varies", musashi: "World-class" },
  { label: "Tech stack", agency: "Legacy/WordPress", freelancer: "Varies", musashi: "Modern (Next.js/AI)" },
  { label: "Scalability", agency: "Costly updates", freelancer: "Limited", musashi: "Enterprise-ready" },
  { label: "Pricing", agency: "$50k - $150k+", freelancer: "Hourly", musashi: "Fixed price per scope" }
];

export const FAQS = [
  {
    question: "¿Cómo es la forma de pago?",
    answer: "Trabajamos con precio fijo por alcance definido. El proyecto se paga una sola vez e incluye soporte de estabilidad sobre la versión entregada."
  },
  {
    question: "¿Cuál es su tiempo de entrega habitual?",
    answer: "Entregamos por fases cortas. Un MVP o sitio pequeño suele estar listo en semanas, no meses, según alcance."
  },
  {
    question: "¿Trabajan integraciones con IA?",
    answer: "Sí. Integramos LLMs como GPT y Claude en flujos reales de negocio cuando aportan valor práctico."
  },
  {
    question: "¿El soporte está incluido?",
    answer: "Sí. Incluimos soporte continuo para estabilidad y monitoreo general. Nuevas funcionalidades o cambios de alcance se cotizan aparte."
  },
  {
    question: "¿Qué stack tecnológico utilizan?",
    answer: "Elegimos el stack según cada caso para entregar rapidez, mantenibilidad y buena relación costo-beneficio."
  }
];

export const FAQS_EN = [
  {
    question: "How does pricing work?",
    answer: "We work with fixed pricing per defined scope. The project is paid once and includes stability support for the delivered version."
  },
  {
    question: "What is your typical delivery timeline?",
    answer: "We deliver in short phases. A small website or MVP is usually shipped in weeks, not months, depending on scope."
  },
  {
    question: "Do you handle AI integrations?",
    answer: "Yes. We integrate LLMs such as GPT and Claude into real business workflows when they provide practical value."
  },
  {
    question: "Is support included?",
    answer: "Yes. Ongoing stability support is included. New features or scope changes are quoted separately."
  },
  {
    question: "What tech stack do you use?",
    answer: "We choose the stack per project to optimize speed, maintainability, and cost-effectiveness."
  }
];
